// src/utils/visibilityEngine.ts
// ==== Utworzony w Krok 3


// Context-based JavaScript Expression Evaluator
// Phase 1: c.responses() support

import { FormData } from '../types/forms';

// Context object interface - extensible for future phases
interface FormContext {
  responses: (fieldId: string) => { value: any };
  // TODO Phase 2: cultureName: string;
  // TODO Phase 3: user: UserInfo; 
  // TODO Phase 4: platform: PlatformInfo;
  // TODO Phase 5: meta: FormMetaInfo;
  // TODO Phase 6: remoteApi: RemoteApiService;
  // TODO Phase 7: setConfig: (config: any) => void;
}

export const evaluateVisibility = (
  visibility: boolean | string | string[] | undefined,
  formData: FormData
): boolean => {
  // Static visibility values
  if (visibility === undefined || visibility === true) {
    return true;
  }
  
  if (visibility === false) {
    return false;
  }
  
  // JavaScript expression evaluation
  if (typeof visibility === 'string') {
    return evaluateExpression(visibility, formData);
  }
  
  // Multi-line array format
  if (Array.isArray(visibility)) {
    const multiLineExpression = visibility.join('\n');
    return evaluateExpression(multiLineExpression, formData);
  }
  
  return true;
};

const evaluateExpression = (expression: string, formData: FormData): boolean => {
  try {
    // Create form context - Phase 1: only responses
    const context: FormContext = createFormContext(formData);
    
    // Process expression format
    const jsCode = processExpressionFormat(expression);
    
    // Debug logging
    console.log('Visibility evaluation:', {
      original: expression,
      processed: jsCode,
      context: context
    });
    
    // Execute in context
    const result = executeInContext(jsCode, context);
    console.log('→ Result:', result);
    
    return Boolean(result);
  } catch (error) {
    console.warn('Error evaluating visibility expression:', expression, error);
    return true; // Show by default on error
  }
};

const createFormContext = (formData: FormData): FormContext => {
  return {
    responses: (fieldId: string) => ({
      value: formData[fieldId]
    })
    // Phase 2 TODO: cultureName: getCurrentCulture(),
    // Phase 3 TODO: user: getCurrentUser(),
    // etc.
  };
};

const processExpressionFormat = (expression: string): string => {
  let jsCode = expression.trim();
  
  // Handle "= expression" format → "return expression"
  if (jsCode.startsWith('=')) {
    jsCode = 'return ' + jsCode.substring(1).trim();
  }
  
  // If no return statement, wrap in return
  if (!jsCode.includes('return') && !jsCode.includes('\n')) {
    jsCode = 'return ' + jsCode;
  }
  
  return jsCode;
};

const executeInContext = (jsCode: string, context: FormContext): any => {
  // Create execution function with context as parameters
  const contextKeys = Object.keys(context);
  const contextValues = Object.values(context);
  
  // Add dayjs support (Phase 1 - basic, can be extended)
  const dayjs = createBasicDayjs();
  
  // Create function with context variables in scope
  const func = new Function(
    'c',           // context object
    'dayjs',       // dayjs library 
    jsCode
  );
  
  // Execute with context
  return func(context, dayjs);
};

// Basic dayjs mock - TODO: replace with real dayjs in future phase
const createBasicDayjs = () => {
  return {
    // Basic implementation - extend as needed
    now: () => new Date(),
    format: (date: Date, format: string) => date.toISOString()
  };
};

// Debug helper
export const debugVisibility = (
  expression: string | string[], 
  formData: FormData
): void => {
  console.group('🔍 Visibility Debug');
  console.log('Expression:', expression);
  console.log('Form Data:', formData);
  
  try {
    const result = evaluateVisibility(expression, formData);
    console.log('✅ Result:', result);
  } catch (error) {
    console.error('❌ Error:', error);
  }
  
  console.groupEnd();
};

// Export for testing
export const _internal = {
  createFormContext,
  processExpressionFormat,
  executeInContext
};